import React, { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
  DialogTitle,
  CircularProgress,
  TextField,
  Alert,
} from '@mui/material';
import { useMutation } from '@apollo/client';
import { CREATE_PRESIGNED_S3_URL, CREATE_CASHFLOW_TEMPLATE } from '../../operations';
import { styles } from './styles';
import { useCashflowTemplateTypes } from '../../../../../../hooks/useCashflowTemplateTypes';
import { DEFAULT_TEMPLATE_TYPE, MODEL_TYPES } from '../../../../../../utils/cashflowConstants';

const DEFAULT_MODEL_TYPE = Object.values(MODEL_TYPES).find((m) => m.default)?.typeName;

export const CCFTemplateModal = ({ open, onClose, onSuccess }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [templateType, setTemplateType] = useState(null);
  const [modelType, setModelType] = useState(DEFAULT_MODEL_TYPE);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const { templateTypes, loading: loadingTemplateTypes } = useCashflowTemplateTypes();
  const defaultTemplateType = useMemo(
    () => templateTypes.find((type) => type.id.toUpperCase() === DEFAULT_TEMPLATE_TYPE)?.id || null,
    [templateTypes],
  );

  const [createPresignedS3Url] = useMutation(CREATE_PRESIGNED_S3_URL);
  const [createTemplate] = useMutation(CREATE_CASHFLOW_TEMPLATE);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
  };

  const handleTemplateTypeChange = (event) => {
    const type = event.target.value;
    setTemplateType(type);
  };

  const handleModelTypeChange = (event) => {
    const type = event.target.value;
    setModelType(type);

    if (type === MODEL_TYPES.clm.typeName) {
      setTemplateType(defaultTemplateType);
    }
  };

  const handleSubmit = async () => {
    try {
      setUploading(true);
      setError(null);

      // Step 1: Get presigned URL
      const { data: presignedData } = await createPresignedS3Url({
        variables: {
          modelType: modelType.toLowerCase(),
        },
      });

      if (presignedData.createPresignedS3Url.errors?.length > 0) {
        throw new Error(presignedData.createPresignedS3Url.errors[0]);
      }

      const { s3Key, s3Bucket, uuid, signedUrl } = presignedData.createPresignedS3Url;
      const { url, fields } = signedUrl;

      // Create a FormData object
      const formData = new FormData();

      // Add all the fields from the presigned URL
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', selectedFile);

      // Step 2: Upload file to S3
      const uploadResponse = await fetch(url, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3');
      }

      // Step 3: Create template
      const { data: templateData } = await createTemplate({
        variables: {
          input: {
            name,
            description,
            modelType,
            s3Key,
            s3Bucket,
            uuid,
            templateType,
          },
        },
      });

      if (templateData.createCashflowTemplate.errors?.length > 0) {
        throw new Error(templateData.createCashflowTemplate.errors[0]);
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} sx={styles.dialog}>
      <DialogTitle>Upload New Template</DialogTitle>

      <FormControl sx={styles.formControl}>
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
      </FormControl>

      <FormControl sx={styles.formControl}>
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
        />
      </FormControl>

      <FormControl sx={styles.formControl}>
        <Button variant="contained" component="label" sx={styles.uploadButton}>
          Choose File
          <input type="file" hidden onChange={handleFileUpload} accept=".xlsx,.xls,.csv" />
        </Button>
        {selectedFile && (
          <Typography variant="body2">Selected file: {selectedFile.name}</Typography>
        )}
      </FormControl>

      <FormControl sx={styles.formControl} fullWidth>
        <InputLabel id="model-type-label">Model Type</InputLabel>
        <Select
          labelId="model-type-label"
          value={modelType}
          onChange={(e) => handleModelTypeChange(e)}
          label="Model Type"
        >
          {Object.entries(MODEL_TYPES).map(([key, value]) => (
            <MenuItem key={key} value={value.typeName}>
              {value.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={styles.formControl}>
        <RadioGroup value={templateType} onChange={handleTemplateTypeChange}>
          {templateTypes.map((type) => (
            <FormControlLabel
              key={type.id}
              value={type.id}
              control={<Radio />}
              label={type.name}
              disabled={modelType === MODEL_TYPES.clm.typeName}
            />
          ))}
        </RadioGroup>
      </FormControl>

      <div style={styles.actionButtons}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
            {error}
          </Alert>
        )}
        <Button onClick={onClose} sx={styles.button} disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!selectedFile || uploading || loadingTemplateTypes}
          sx={styles.button}
        >
          {uploading ? <CircularProgress size={24} /> : 'Upload'}
        </Button>
      </div>
    </Dialog>
  );
};
